import React, { useState } from 'react';
import { Button, Input } from 'reactstrap';

// How a model was run -- effort, thinking, deep research -- as the person pasting
// states it. The AI cannot see these settings, so the answer is never asked.
// The server keeps each model's last setting as its next default, and offers a
// setting typed in here again next time. See backend platform/models/configs.js.

const LAST = '__last__', NEW = '__new__', NONE = '__none__';

/**
 * Choosing a setting. `value` undefined is "this model's last setting", which
 * the server fills in once it knows the model; '' is "not stated".
 */
export function ConfigSelect({ id, value, onChange, options = [], disabled, allowLast = true, autoFocus }) {
  const [adding, setAdding] = useState(false);
  const known = value === undefined || value === '' || options.includes(value);
  const selected = adding ? NEW : value === undefined ? LAST : value === '' ? NONE : value;
  return <div className="d-flex flex-wrap gap-2">
    <Input id={id} type="select" bsSize="sm" style={{ maxWidth: 260 }} disabled={disabled} value={selected} autoFocus={autoFocus}
      onChange={e => {
        const v = e.target.value;
        setAdding(v === NEW);
        if (v === NEW) onChange('');
        else onChange(v === LAST ? undefined : v === NONE ? '' : v);
      }}>
      {allowLast && <option value={LAST}>This model's last setting</option>}
      {options.map(o => <option key={o} value={o}>{o}</option>)}
      {!known && !adding && <option value={value}>{value}</option>}
      <option value={NONE}>Not stated</option>
      <option value={NEW}>Add a new setting…</option>
    </Input>
    {adding && <Input aria-label="New setting" bsSize="sm" style={{ maxWidth: 280 }} maxLength={60} disabled={disabled} autoFocus
      placeholder="e.g. Effort: xhigh, Pro mode" value={value || ''} onChange={e => onChange(e.target.value)} />}
  </div>;
}

/** What the server will file an answer under, once it has been parsed. */
export function ConfigNote({ preview }) {
  if (!preview) return null;
  const how = preview.model_config_source === 'last' ? ` (${preview.model_name}'s last setting)` : '';
  return <p className="fs-13 mb-1">Run at: <strong>{preview.model_config || 'not stated'}</strong>{how}.</p>;
}

/**
 * A recorded answer's setting, changeable in place by its batch owner or an
 * administrator. Calls `onSaved` with the new setting.
 */
export function AnswerConfig({ prediction, request, options, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false), [value, setValue] = useState(''), [busy, setBusy] = useState(false), [error, setError] = useState('');
  // The page's list once it has loaded; fetched here only if the page has none.
  const [fetched, setFetched] = useState(null);
  const list = options && options.length ? options : fetched;
  const stop = e => e.stopPropagation();
  const open = e => {
    e.preventDefault(); stop(e);
    setValue(prediction.model_config || ''); setError(''); setEditing(true);
    if (!list) request('/options').then(o => setFetched(o.modelConfigs?.options || [])).catch(() => setFetched([]));
  };
  const save = async e => {
    stop(e); setBusy(true); setError('');
    try {
      const r = await request(`/predictions/${prediction.id}/config`, { config: value });
      setEditing(false);
      onSaved?.(r.model_config);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };
  if (!editing) {
    return <span className="text-muted fs-12">
      {prediction.model_config || (canEdit ? 'setting not stated' : null)}
      {canEdit && <button type="button" className="btn btn-link btn-sm p-0 ms-1 align-baseline" title="Change how this model was run" aria-label={`Change how ${prediction.model_name} was run`} onClick={open}><i className="ri-pencil-line" /></button>}
    </span>;
  }
  return <span className="d-inline-flex flex-wrap align-items-center gap-1" onClick={stop}>
    <ConfigSelect value={value} onChange={v => setValue(v || '')} options={list || []} disabled={busy} allowLast={false} autoFocus />
    <Button size="sm" color="primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</Button>
    <Button size="sm" color="light" disabled={busy} onClick={e => { stop(e); setEditing(false); }}>Cancel</Button>
    {error && <span className="text-danger fs-12">{error}</span>}
  </span>;
}

/** Which answers on a page are ticked for a change made to them all at once. */
export function useSelection() {
  const [ids, setIds] = useState(() => new Set());
  const toggle = id => setIds(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  const setAll = (list, on) => setIds(prev => { const next = new Set(prev); list.forEach(id => (on ? next.add(id) : next.delete(id))); return next; });
  const clear = () => setIds(new Set());
  return { ids, has: id => ids.has(id), toggle, setAll, clear };
}

/** A header checkbox that ticks or clears every changeable answer on the page. */
export function SelectAll({ selection, ids }) {
  const ticked = ids.filter(id => selection.has(id)).length;
  return <input type="checkbox" className="form-check-input" aria-label="Select all on this page" disabled={!ids.length}
    checked={!!ids.length && ticked === ids.length} ref={el => { if (el) el.indeterminate = ticked > 0 && ticked < ids.length; }}
    onClick={e => e.stopPropagation()} onChange={e => selection.setAll(ids, e.target.checked)} />;
}

/** One row's checkbox. */
export function SelectOne({ selection, prediction }) {
  return <input type="checkbox" className="form-check-input" aria-label={`Select ${prediction.model_name}`} checked={selection.has(prediction.id)}
    onClick={e => e.stopPropagation()} onChange={() => selection.toggle(prediction.id)} />;
}

/**
 * Set one setting on every ticked answer. Calls `onDone(config, updatedIds)`;
 * answers that could not be changed are named and stay ticked.
 */
export function BulkConfig({ selection, request, options, onDone }) {
  const [value, setValue] = useState(''), [busy, setBusy] = useState(false), [error, setError] = useState('');
  const n = selection.ids.size;
  if (!n) return null;
  const apply = async () => {
    setBusy(true); setError('');
    try {
      const r = await request('/predictions/config', { ids: [...selection.ids], config: value });
      selection.setAll(r.updated, false);
      onDone?.(r.model_config, r.updated);
      if (r.failed.length) setError(`${r.failed.length} not changed: ${[...new Set(r.failed.map(f => f.error))].join('; ')}`);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  return <div className="d-flex flex-wrap align-items-center gap-2 mb-2 p-2 border rounded bg-light" aria-label="Change selected">
    <span className="fs-13">{n} selected · set how they were run:</span>
    <ConfigSelect value={value} onChange={v => setValue(v || '')} options={options || []} disabled={busy} allowLast={false} />
    <Button size="sm" color="primary" disabled={busy} onClick={apply}>{busy ? 'Saving…' : `Set for ${n}`}</Button>
    <Button size="sm" color="light" disabled={busy} onClick={selection.clear}>Clear selection</Button>
    {error && <span className="text-danger fs-12">{error}</span>}
  </div>;
}
